/**
 * AddEditEventInstanceDialog
 * Dialog for creating/editing event instances.
 */

import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCreateEventInstance, useUpdateEventInstance, useEcosystemEventSeries } from '@/hooks/useEcosystemData';
import { ECOSYSTEM_SOURCE_TYPE_LABELS, type EcosystemSourceType } from '@/types/ecosystem';

interface AddEditEventInstanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instance?: any | null;
  defaultSeriesId?: string;
}

export function AddEditEventInstanceDialog({
  open,
  onOpenChange,
  instance,
  defaultSeriesId,
}: AddEditEventInstanceDialogProps) {
  const isEditMode = !!instance;

  const [seriesId, setSeriesId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [timezone, setTimezone] = useState('America/Chicago');
  const [venueName, setVenueName] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('US');
  const [eventUrl, setEventUrl] = useState('');
  const [registrationUrl, setRegistrationUrl] = useState('');
  const [audience, setAudience] = useState('');
  const [tracks, setTracks] = useState('');
  const [sourceType, setSourceType] = useState<string>('manual');
  const [stableKey, setStableKey] = useState('');

  const createInstance = useCreateEventInstance();
  const updateInstance = useUpdateEventInstance();
  const { data: allSeries } = useEcosystemEventSeries();

  useEffect(() => {
    if (open && instance) {
      setSeriesId(instance.series_id || '');
      setStartDate(instance.start_date || '');
      setEndDate(instance.end_date || '');
      setTimezone(instance.timezone || 'America/Chicago');
      setVenueName(instance.venue_name || '');
      setCity(instance.city || '');
      setState(instance.state || '');
      setCountry(instance.country || 'US');
      setEventUrl(instance.event_url || '');
      setRegistrationUrl(instance.registration_url || '');
      setAudience(instance.audience?.join(', ') || '');
      setTracks(instance.tracks?.join(', ') || '');
      setSourceType(instance.source_type || 'manual');
      setStableKey(instance.stable_key || '');
    } else if (open) {
      setSeriesId(defaultSeriesId || '');
      setStartDate('');
      setEndDate('');
      setTimezone('America/Chicago');
      setVenueName('');
      setCity('');
      setState('');
      setCountry('US');
      setEventUrl('');
      setRegistrationUrl('');
      setAudience('');
      setTracks('');
      setSourceType('manual');
      setStableKey('');
    }
  }, [open, instance, defaultSeriesId]);

  const handleSubmit = async () => {
    if (!seriesId) {
      toast.error('Event series is required');
      return;
    }

    try {
      if (isEditMode) {
        await updateInstance.mutateAsync({
          data: {
            instanceId: instance.id,
            start_date: startDate || null,
            end_date: endDate || null,
            timezone: timezone || null,
            venue_name: venueName.trim() || null,
            city: city.trim() || null,
            state: state.trim() || null,
            country: country.trim() || 'US',
            event_url: eventUrl.trim() || null,
            registration_url: registrationUrl.trim() || null,
            audience,
            tracks,
            source_type: sourceType,
          },
        });
        toast.success('Event instance updated');
      } else {
        await createInstance.mutateAsync({
          data: {
            series_id: seriesId,
            start_date: startDate || null,
            end_date: endDate || null,
            timezone: timezone || null,
            venue_name: venueName.trim() || null,
            city: city.trim() || null,
            state: state.trim() || null,
            country: country.trim() || 'US',
            event_url: eventUrl.trim() || null,
            registration_url: registrationUrl.trim() || null,
            audience,
            tracks,
            source_type: sourceType,
            stable_key: stableKey.trim() || null,
          },
        });
        toast.success('Event instance created');
      }
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to save event instance');
    }
  };

  const isSubmitting = createInstance.isPending || updateInstance.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Event Instance' : 'Add Event Instance'}</DialogTitle>
          <DialogDescription>
            {isEditMode ? 'Update this event occurrence.' : 'Create a new dated occurrence of an event series.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Event Series *</Label>
            <Select value={seriesId} onValueChange={setSeriesId} disabled={isEditMode}>
              <SelectTrigger><SelectValue placeholder="Select series" /></SelectTrigger>
              <SelectContent>
                {(allSeries || []).map((s: any) => (
                  <SelectItem key={s.id} value={s.id}>{s.series_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input id="startDate" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input id="endDate" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="venueName">Venue</Label>
              <Input id="venueName" value={venueName} onChange={(e) => setVenueName(e.target.value)} placeholder="Convention Center" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="city">City</Label>
              <Input id="city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Kansas City" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="state">State</Label>
              <Input id="state" value={state} onChange={(e) => setState(e.target.value)} placeholder="MO" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="country">Country</Label>
              <Input id="country" value={country} onChange={(e) => setCountry(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="timezone">Timezone</Label>
              <Input id="timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="eventUrl">Event URL</Label>
              <Input id="eventUrl" type="url" value={eventUrl} onChange={(e) => setEventUrl(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="regUrl">Registration URL</Label>
              <Input id="regUrl" type="url" value={registrationUrl} onChange={(e) => setRegistrationUrl(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="audience">Audience</Label>
              <Input id="audience" value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="technician, advisor" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="tracks">Tracks</Label>
              <Input id="tracks" value={tracks} onChange={(e) => setTracks(e.target.value)} placeholder="diagnostics, electrical" />
            </div>
          </div>

          <div className="grid gap-2">
            <Label>Source Type</Label>
            <Select value={sourceType} onValueChange={setSourceType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.entries(ECOSYSTEM_SOURCE_TYPE_LABELS) as [EcosystemSourceType, string][]).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!isEditMode && (
            <div className="grid gap-2">
              <Label htmlFor="stableKey">Stable Key</Label>
              <Input id="stableKey" value={stableKey} onChange={(e) => setStableKey(e.target.value)} placeholder="Auto-generated" className="font-mono text-sm" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEditMode ? 'Save Changes' : 'Create Instance'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
